<template>
  <div class="square-page">
    <section class="square-stats">
      <button
        v-for="item in statCards"
        :key="item.key"
        type="button"
        class="stat-card"
        :class="[`stat-card--${item.tone}`, { 'stat-card--active': activeStat === item.key }]"
        :disabled="!item.filter"
        @click="applyStatFilter(item)"
      >
        <span class="stat-card__top">
          <span>{{ item.label }}</span>
          <i>{{ item.icon }}</i>
        </span>
        <strong>{{ formatNumber(item.value) }}</strong>
        <small>{{ item.hint }}</small>
      </button>
    </section>

    <section class="square-card">
      <div class="square-list-toolbar">
        <div class="square-list-title">
          <strong>广场帖子</strong>
          <span>当前筛选共 {{ total }} 条</span>
        </div>
        <div class="square-list-toolbar__actions">
          <button class="ghost-button" type="button" :disabled="loading || loadingStats" @click="refreshAll">
            {{ loading || loadingStats ? "刷新中" : "刷新" }}
          </button>
          <div class="view-switch" role="group" aria-label="切换帖子布局">
            <button type="button" :class="{ active: viewMode === 'card' }" @click="setViewMode('card')">卡片</button>
            <button type="button" :class="{ active: viewMode === 'list' }" @click="setViewMode('list')">列表</button>
          </div>
        </div>
      </div>

      <div class="square-filters">
        <input
          v-model="filters.keyword"
          class="filter-input"
          type="text"
          placeholder="搜索：帖子ID / 正文 / 作者昵称"
          @keyup.enter="reload"
        />
        <select v-model="filters.visibilityScope" class="filter-select" @change="reload">
          <option value="">全部可见范围</option>
          <option value="public">公开</option>
          <option value="family">家庭</option>
          <option value="private">私密</option>
        </select>
        <select v-model="filters.auditStatus" class="filter-select" @change="reload">
          <option value="">全部审核状态</option>
          <option value="PASS">已通过</option>
          <option value="REVIEWING">审核中</option>
          <option value="REJECTED">未通过</option>
        </select>
        <select v-model="filters.deleted" class="filter-select" @change="reload">
          <option value="">全部帖子</option>
          <option value="0">未删除</option>
          <option value="1">已删除</option>
        </select>
        <button class="primary-button" type="button" @click="reload">搜索</button>
      </div>

      <div v-if="loading" class="list-state">正在加载广场帖子…</div>
      <div v-else-if="!rows.length" class="list-state">没有符合条件的帖子。</div>
      <div v-else class="post-list" :class="{ 'post-list--cards': viewMode === 'card' }">
        <article
          v-for="row in rows"
          :key="String(row.id)"
          class="post-row"
          :class="{ 'post-row--card': viewMode === 'card' }"
        >
          <div class="post-media">
            <img
              v-if="coverUrl(row) && !mediaErrors[row.id]"
              class="post-thumb"
              :src="coverSource(row)"
              alt=""
              @error="handleMediaError(row)"
            />
            <div v-else-if="coverUrl(row)" class="post-thumb post-thumb--empty">图片失效</div>
            <div v-else class="post-thumb post-thumb--empty">{{ row.mediaType === "video" ? "视频" : "无图" }}</div>
            <span v-if="mediaCount(row) > 1" class="media-count">{{ mediaCount(row) }} 张</span>
          </div>
          <div class="post-main">
            <div class="post-head">
              <span class="post-id">#{{ row.id }}</span>
              <span class="post-author">{{ row.authorName || "用户" }}（UID {{ row.ownerUserId }}）</span>
              <span class="post-time">{{ formatTime(row.createdAt) }}</span>
            </div>
            <div class="post-content">{{ row.content || "（无正文）" }}</div>
            <div class="post-tags">
              <span class="tag">{{ visibilityLabel(row.visibilityScope) }}</span>
              <span class="tag" v-if="row.activityCampaignId">活动帖</span>
              <span class="stat">赞 {{ row.likeCount || 0 }}</span>
              <span class="stat">评 {{ row.commentCount || 0 }}</span>
              <span class="stat">藏 {{ row.favoriteCount || 0 }}</span>
            </div>
          </div>
          <div class="post-side">
            <span class="status-pill" :class="statusClass(row)">
              {{ statusLabel(row) }}
            </span>
            <button class="link-button" type="button" @click="updateStatus(row, 'PASS')" v-if="row.deleted === 1 || row.contentAuditStatus !== 'PASS'">
              上架
            </button>
            <button class="link-button" type="button" @click="updateStatus(row, 'REJECTED')" v-if="row.deleted === 0 && row.contentAuditStatus === 'PASS'">
              下架
            </button>
            <button class="link-button link-button--danger" type="button" @click="removePost(row)" v-if="row.deleted === 0">删除</button>
            <button class="link-button" type="button" @click="restorePost(row)" v-if="row.deleted === 1">恢复</button>
          </div>
        </article>
      </div>

      <div v-if="total > pageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
        <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
        <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
      </div>

      <div v-if="message" class="page-message" :class="{ 'page-message--error': messageError }">{{ message }}</div>
    </section>
  </div>
</template>

<script>
import { resolveBackendAssetFallbackUrl, resolveBackendAssetUrl } from "../utils/apiEndpoint"
import {
  deleteAdminSquarePost,
  fetchAdminSquarePosts,
  fetchAdminSquareStats,
  restoreAdminSquarePost,
  updateAdminSquarePostStatus
} from "../api/squareAdmin"

const emptyStats = () => ({
  total: 0,
  published: 0,
  reviewing: 0,
  hidden: 0,
  deleted: 0,
  todayPosts: 0,
  interactions: 0
})

export default {
  name: "SquareManageView",
  data() {
    const query = this.$route.query || {}
    return {
      loading: false,
      loadingStats: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      viewMode: localStorage.getItem("pachong_square_view_mode") === "list" ? "list" : "card",
      stats: emptyStats(),
      activeStat: "total",
      mediaFallbacks: {},
      mediaErrors: {},
      filters: {
        keyword: String(query.keyword || ""),
        visibilityScope: String(query.visibilityScope || ""),
        auditStatus: String(query.auditStatus || ""),
        deleted: query.deleted === undefined ? "" : String(query.deleted)
      },
      message: "",
      messageError: false
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    },
    statCards() {
      return [
        {
          key: "total",
          label: "全部帖子",
          icon: "全",
          value: this.stats.total,
          hint: `今日新增 ${this.stats.todayPosts}`,
          tone: "blue",
          filter: { auditStatus: "", deleted: "" }
        },
        {
          key: "published",
          label: "广场可见",
          icon: "上",
          value: this.stats.published,
          hint: "已通过且未删除",
          tone: "green",
          filter: { auditStatus: "PASS", deleted: "0" }
        },
        {
          key: "reviewing",
          label: "审核中",
          icon: "审",
          value: this.stats.reviewing,
          hint: "等待审核回调",
          tone: "amber",
          filter: { auditStatus: "REVIEWING", deleted: "0" }
        },
        {
          key: "hidden",
          label: "已下架",
          icon: "下",
          value: this.stats.hidden,
          hint: "审核驳回或人工下架",
          tone: "rose",
          filter: { auditStatus: "REJECTED", deleted: "1" }
        },
        {
          key: "deleted",
          label: "已删除",
          icon: "删",
          value: this.stats.deleted,
          hint: "可从列表恢复",
          tone: "slate",
          filter: { auditStatus: "", deleted: "1" }
        },
        {
          key: "interactions",
          label: "累计互动",
          icon: "互",
          value: this.stats.interactions,
          hint: "点赞、评论与收藏",
          tone: "purple",
          filter: null
        }
      ]
    }
  },
  created() {
    this.refreshAll()
  },
  watch: {
    "$route.query"(query) {
      this.filters = {
        keyword: String(query.keyword || ""),
        visibilityScope: String(query.visibilityScope || ""),
        auditStatus: String(query.auditStatus || ""),
        deleted: query.deleted === undefined ? "" : String(query.deleted)
      }
      this.page = 1
      this.refreshAll()
    }
  },
  methods: {
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN")
    },
    mediaCount(row) {
      return Array.isArray(row && row.imageUrls) ? row.imageUrls.length : 0
    },
    coverUrl(row) {
      if (!row) return ""
      if (row.mediaType === "video") {
        return row.videoCoverUrl ? this.resolveFileUrl(row.videoCoverUrl) : ""
      }
      const list = row.imageUrls || []
      return list.length > 0 ? this.resolveFileUrl(list[0]) : ""
    },
    coverSource(row) {
      if (!row) return ""
      if (row.mediaType === "video") {
        return this.mediaFallbacks[row.id]
          ? resolveBackendAssetFallbackUrl(row.videoCoverUrl)
          : this.resolveFileUrl(row.videoCoverUrl)
      }
      const list = row.imageUrls || []
      if (!list.length) return ""
      return this.mediaFallbacks[row.id]
        ? resolveBackendAssetFallbackUrl(list[0])
        : this.resolveFileUrl(list[0])
    },
    handleMediaError(row) {
      if (!this.mediaFallbacks[row.id]) {
        this.$set(this.mediaFallbacks, row.id, true)
        return
      }
      this.$set(this.mediaErrors, row.id, true)
    },
    formatTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    visibilityLabel(scope) {
      if (scope === "family") return "家庭可见"
      if (scope === "private") return "仅自己"
      return "公开"
    },
    statusLabel(row) {
      if (row.deleted === 1) return "已删除"
      if (row.contentAuditStatus === "PASS") return "已上架"
      if (row.contentAuditStatus === "REJECTED") return "已下架"
      return "审核中"
    },
    statusClass(row) {
      if (row.deleted === 1) return "status-pill--deleted"
      if (row.contentAuditStatus === "PASS") return "status-pill--pass"
      if (row.contentAuditStatus === "REJECTED") return "status-pill--rejected"
      return "status-pill--reviewing"
    },
    setMessage(text, isError) {
      this.message = text
      this.messageError = !!isError
    },
    setViewMode(mode) {
      if (mode !== "card" && mode !== "list") return
      this.viewMode = mode
      localStorage.setItem("pachong_square_view_mode", mode)
    },
    async refreshAll() {
      await Promise.all([this.reload(), this.loadStats()])
    },
    async loadStats() {
      if (this.loadingStats) return
      this.loadingStats = true
      try {
        const res = await fetchAdminSquareStats()
        this.stats = { ...emptyStats(), ...(res.data || {}) }
      } catch (error) {
        this.setMessage((error && error.message) || "广场数据概览加载失败", true)
      } finally {
        this.loadingStats = false
      }
    },
    applyStatFilter(item) {
      if (!item.filter || this.loading) return
      this.activeStat = item.key
      this.filters.auditStatus = item.filter.auditStatus
      this.filters.deleted = item.filter.deleted
      this.page = 1
      this.reload()
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchAdminSquarePosts({
          keyword: this.filters.keyword || undefined,
          visibilityScope: this.filters.visibilityScope || undefined,
          auditStatus: this.filters.auditStatus || undefined,
          deleted: this.filters.deleted === "" ? undefined : Number(this.filters.deleted),
          page: this.page,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.rows = data.records || []
        this.total = Number(data.total || 0)
      } catch (error) {
        this.setMessage((error && error.message) || "广场帖子加载失败", true)
      } finally {
        this.loading = false
      }
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.reload()
    },
    async updateStatus(row, auditStatus) {
      try {
        const res = await updateAdminSquarePostStatus(row.id, auditStatus)
        this.setMessage(res.message || "操作成功", false)
        await this.refreshAll()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async removePost(row) {
      if (!window.confirm(`确定删除帖子 #${row.id} 吗？删除后小程序里不再展示。`)) {
        return
      }
      try {
        await deleteAdminSquarePost(row.id)
        this.setMessage("帖子已删除", false)
        await this.refreshAll()
      } catch (error) {
        this.setMessage((error && error.message) || "删除失败", true)
      }
    },
    async restorePost(row) {
      try {
        await restoreAdminSquarePost(row.id)
        this.setMessage("帖子已恢复并上架", false)
        await this.refreshAll()
      } catch (error) {
        this.setMessage((error && error.message) || "恢复失败", true)
      }
    }
  }
}
</script>

<style scoped>
.square-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.square-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  border-radius: 16px;
  background: linear-gradient(120deg, #f0f7f2 0%, #ffffff 60%);
  border: 1px solid #dcebe1;
}

.square-toolbar__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.square-toolbar__desc {
  margin-top: 8px;
  max-width: 760px;
  font-size: 13px;
  line-height: 20px;
  color: #6b7280;
}

.square-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.square-count {
  font-size: 13px;
  color: #4b7a5c;
}

.square-stats {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.stat-card {
  min-width: 0;
  padding: 16px;
  text-align: left;
  border: 1px solid #e7ebf0;
  border-radius: 14px;
  background: #ffffff;
  cursor: pointer;
  transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}

.stat-card:not(:disabled):hover {
  transform: translateY(-2px);
  border-color: #cbd5e1;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.07);
}

.stat-card:disabled {
  cursor: default;
}

.stat-card--active {
  border-color: #1f6b4f;
  box-shadow: 0 0 0 2px rgba(31, 107, 79, 0.08);
}

.stat-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #64748b;
  font-size: 12px;
}

.stat-card__top i {
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 8px;
  font-style: normal;
  font-size: 11px;
  font-weight: 700;
}

.stat-card strong {
  display: block;
  margin-top: 12px;
  color: #17202b;
  font-size: 25px;
  line-height: 1;
}

.stat-card small {
  display: block;
  margin-top: 8px;
  overflow: hidden;
  color: #94a3b8;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-card--blue .stat-card__top i { background: #eaf2ff; color: #2563eb; }
.stat-card--green .stat-card__top i { background: #e8f7ee; color: #18864b; }
.stat-card--amber .stat-card__top i { background: #fff3dd; color: #b45309; }
.stat-card--rose .stat-card__top i { background: #fff0ed; color: #c2412d; }
.stat-card--slate .stat-card__top i { background: #eef2f6; color: #526171; }
.stat-card--purple .stat-card__top i { background: #f3edff; color: #7c3aed; }

.square-card {
  padding: 20px 22px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eceff3;
}

.square-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.square-list-title {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.square-list-title strong {
  color: #17202b;
  font-size: 15px;
}

.square-list-title span {
  color: #94a3b8;
  font-size: 12px;
}

.square-list-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.view-switch {
  display: inline-flex;
  padding: 3px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}

.view-switch button {
  min-width: 52px;
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  cursor: pointer;
}

.view-switch button.active {
  background: #ffffff;
  color: #17202b;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.square-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  color: #1f2937;
  box-sizing: border-box;
}

.filter-input {
  flex: 1;
  max-width: 300px;
}

.filter-input--narrow {
  max-width: 150px;
}

.primary-button,
.ghost-button {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
}

.primary-button {
  background: #111827;
  color: #ffffff;
}

.primary-button:disabled,
.ghost-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.ghost-button {
  background: #ffffff;
  border-color: #dfe3e8;
  color: #374151;
}

.list-state {
  padding: 26px 0;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.post-list {
  display: flex;
  flex-direction: column;
}

.post-list--cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.post-row {
  display: flex;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f1f3f6;
}

.post-row--card {
  position: relative;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  border: 1px solid #e8edf3;
  border-radius: 16px;
  background: #ffffff;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.post-row--card:hover {
  transform: translateY(-2px);
  border-color: #d5dee8;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.09);
}

.post-row--card .post-media {
  width: 100%;
}

.post-row--card .post-thumb {
  width: 100%;
  height: 158px;
  border-radius: 0;
}

.post-row--card .post-main {
  padding: 15px 16px 12px;
}

.post-row--card .post-head {
  flex-wrap: wrap;
  gap: 5px 9px;
}

.post-row--card .post-time {
  width: 100%;
}

.post-row--card .post-content {
  display: -webkit-box;
  min-height: 63px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.post-row--card .post-side {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
  padding: 11px 16px 14px;
  border-top: 1px solid #f1f5f9;
}

.post-row--card .status-pill {
  margin-right: auto;
}

.post-media {
  position: relative;
  flex-shrink: 0;
}

.post-thumb {
  width: 78px;
  height: 78px;
  border-radius: 10px;
  object-fit: cover;
  background: #f2f4f7;
}

.post-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #9ca3af;
}

.media-count {
  position: absolute;
  right: 5px;
  bottom: 5px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  color: #ffffff;
  font-size: 10px;
  line-height: 17px;
}

.post-main {
  flex: 1;
  min-width: 0;
}

.post-head {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #8b93a1;
}

.post-id {
  font-weight: 700;
  color: #111827;
}

.post-content {
  margin-top: 6px;
  font-size: 14px;
  line-height: 21px;
  color: #1f2937;
  word-break: break-all;
}

.post-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.tag {
  padding: 2px 8px;
  border-radius: 999px;
  background: #f2f4f7;
  font-size: 12px;
  color: #4b5563;
}

.stat {
  font-size: 12px;
  color: #8b93a1;
}

.post-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.status-pill {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}

.status-pill--pass {
  background: #e7f6ec;
  color: #1d8a4b;
}

.status-pill--reviewing {
  background: #fff5e5;
  color: #b45309;
}

.status-pill--rejected {
  background: #fdecea;
  color: #b23f2c;
}

.status-pill--deleted {
  background: #f2f4f7;
  color: #8b93a1;
}

.link-button {
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
}

.link-button--danger {
  color: #dc2626;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
}

.pager-text {
  font-size: 13px;
  color: #6b7280;
}

.page-message {
  margin-top: 12px;
  font-size: 13px;
  color: #1d8a4b;
}

.page-message--error {
  color: #dc2626;
}

@media (max-width: 1180px) {
  .square-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .square-toolbar {
    flex-direction: column;
  }

  .square-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .square-card {
    padding: 16px 14px;
  }

  .square-list-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .square-list-toolbar__actions {
    justify-content: space-between;
  }

  .filter-input,
  .filter-input--narrow {
    max-width: none;
  }

  .post-row {
    align-items: flex-start;
  }

  .post-side {
    min-width: 52px;
  }
}
</style>
